'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import logWebHeaderHovered from '@alltrails/amplitude/events/logWebHeaderHovered';
import Typography from '@alltrails/core/components/Typography';
import Dropdown from '@alltrails/denali/components/Dropdown';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import Link from '@alltrails/denali/components/Link';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useExposureEvent from '@alltrails/experiments/hooks/useExposureEvent';
import FormattedMessage from '../FormattedMessage';
import { getHeaderLinkOnClickWithAnalytics, HeaderSection } from './headerLinks';
import styles from './styles/styles.module.scss';
import { HeaderBackground } from './types';
import useHeaderLinks, { GetLinkInteractivity } from './useHeaderLinks';

type DropdownProps = {
  background: HeaderBackground;
  index: number;
  getLinkInteractivity: GetLinkInteractivity;
  section: HeaderSection;
};

const DropdownComponent = ({ background, index, getLinkInteractivity, section }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const onMouseEnter = useCallback(() => {
    logWebHeaderHovered({ web_header_position: index, web_header_section: section.webHeaderSection });
    setIsOpen(true);
  }, [index, section.webHeaderSection]);

  const onMouseLeave = useCallback(() => {
    setIsOpen(false);
  }, []);

  const containerMouseHandlers = useMemo(() => ({ onMouseEnter, onMouseLeave }), [onMouseEnter, onMouseLeave]);

  const toggleDropdown = useCallback(() => {
    if (!isOpen) {
      logWebHeaderHovered({ web_header_position: index, web_header_section: section.webHeaderSection });
    }
    setIsOpen(!isOpen);
  }, [index, section.webHeaderSection, isOpen]);

  const anchor = (
    <Dropdown
      className={background === 'transparent-inverted' ? styles.dropdownInverted : styles.dropdown}
      text={section.label}
      variant="flat"
      size="sm"
      onClick={toggleDropdown}
      aria-expanded={isOpen}
      aria-haspopup="menu"
    />
  );
  return (
    <DropdownMenu
      anchor={anchor}
      className={styles.dropdownMenu}
      containerMouseHandlers={containerMouseHandlers}
      isOpen={isOpen}
      onCloseRequest={() => setIsOpen(false)}
      options={section.links.map((link, linkIndex) => {
        const { linkInfo, onClick } = getLinkInteractivity(link);
        const onClickWithAnalytics = getHeaderLinkOnClickWithAnalytics(linkIndex, link.webHeaderDropDownLink, section.webHeaderSection, () => {
          setIsOpen(false);
          onClick?.();
        });
        return {
          className: link.className,
          leadingIcon: link.leadingIcon,
          linkInfo,
          onClick: onClickWithAnalytics,
          title: link.title,
          trailingIcon: link.trailingIcon
        };
      })}
      placement="bottom-end"
    />
  );
};

type DropdownsProps = {
  background: HeaderBackground;
};

const Dropdowns = ({ background }: DropdownsProps) => {
  const experiments = useExperiments();
  const showCreateCta = experiments['web-disco-rm-nav-create']?.value === 'button-text';
  const { headerLinks, getLinkInteractivity, signUpModal } = useHeaderLinks(false);

  const sendExposureEvent = useExposureEvent('web-disco-rm-nav-create');
  useEffect(() => {
    sendExposureEvent();
  }, [sendExposureEvent]);

  return (
    <>
      <div className={styles.dropdowns}>
        {headerLinks.map((section, index) => {
          return (
            <DropdownComponent key={section.id} background={background} index={index} getLinkInteractivity={getLinkInteractivity} section={section} />
          );
        })}
        {showCreateCta && (
          <Typography variant="text100bold">
            <Link href="/explore/custom-routes/new?trigger=nav" noUnderline variant={background === 'transparent-inverted' ? 'inverse' : 'primary'}>
              <FormattedMessage defaultMessage="Create route" />
            </Link>
          </Typography>
        )}
      </div>
      {signUpModal}
    </>
  );
};

export default Dropdowns;
